import React from 'react';
import { Link } from 'react-router-dom';

// Same height-matching fix as CompanyHeader's LogoMark, scaled for text-sm's 20px line box.
const LogoMark: React.FC = () => (
  <svg width="16" height="20" viewBox="0 0 28 34" fill="none" aria-hidden="true">
    <rect x="0" y="22" width="6" height="12" rx="1" fill="#FBFBF9" />
    <rect x="10" y="16" width="6" height="18" rx="1" fill="#FBFBF9" />
    <rect x="20" y="10" width="6" height="24" rx="1" fill="#FBFBF9" />
    <path d="M2 20 Q14 6 26 9" stroke="#3BCC8F" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <path d="M24 5 L28 10 L22 12 Z" fill="#3BCC8F" />
  </svg>
);

interface FooterLink {
  label: string;
  to: string | null;
  internal: boolean;
  sectionId?: string;
}

interface FooterProps {
  company?: boolean;
}

const Footer: React.FC<FooterProps> = ({ company = false }) => {
  const currentYear = new Date().getFullYear();

  const columns = company
    ? [
        {
          heading: 'Tools',
          links: [
            { label: 'Financial Goal Planner', to: '/financial-goal-planner', internal: true },
            { label: 'Compound Calculator', to: '/app/calculator', internal: true },
            { label: 'Investment Tracker', to: null, internal: true },
            { label: 'Estate Planner', to: null, internal: true },
          ],
        },
        {
          heading: 'Company',
          links: [
            { label: 'About', to: '/about', internal: true },
            { label: 'FAQ', to: '/faq', internal: true },
            { label: 'Contact', to: '/contact', internal: true },
          ],
        },
        {
          heading: 'Legal',
          links: [
            { label: 'Privacy Policy', to: '/privacy', internal: true },
            { label: 'Terms of Service', to: '/terms', internal: true },
            { label: 'Delete Account', to: '/delete-account', internal: true },
          ],
        },
      ]
    : [
        {
          heading: 'Product',
          links: [
            { label: 'Features', to: null, internal: false, sectionId: 'features' } as FooterLink,
            { label: 'How it works', to: null, internal: false, sectionId: 'how-it-works' } as FooterLink,
            { label: 'Download', to: null, internal: false, sectionId: 'download' } as FooterLink,
          ],
        },
        {
          heading: 'Legal',
          links: [
            { label: 'Privacy Policy', to: '/privacy', internal: true },
            { label: 'Terms of Service', to: '/terms', internal: true },
            { label: 'Delete Account', to: '/delete-account', internal: true },
          ],
        },
        {
          heading: 'Company',
          links: [
            { label: 'FinInteract', to: '/', internal: true },
            { label: 'All tools', to: '/#tools', internal: true },
          ],
        },
      ];

  return (
    <footer className="bg-ink">
      <div className="max-w-[1080px] mx-auto px-8 sm:px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">

          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <LogoMark />
              <div className="flex flex-col leading-none">
                <span className="font-serif text-paper text-sm">FinInteract</span>
                {!company && (
                  <span className="font-mono text-paper/40 text-[10px] tracking-wide mt-0.5">Financial Tools</span>
                )}
              </div>
            </div>
            <p className="font-sans font-light text-paper/50 text-sm leading-relaxed max-w-xs">
              {company
                ? 'Practical financial tools for people who take their money seriously.'
                : 'A quiet place to grow your money.'}
            </p>
          </div>

          {/* Columns */}
          {columns.map(col => (
            <div key={col.heading}>
              <h3 className="font-sans font-medium text-paper/80 text-sm mb-4 tracking-wide">{col.heading}</h3>
              <ul className="space-y-3">
                {col.links.map(link => (
                  <li key={link.label}>
                    {!link.to ? (
                      <span className="font-sans font-light text-paper/30 text-sm">
                        {link.label}
                        {!link.internal && <span className="ml-1.5 text-[10px] font-mono text-paper/20 uppercase tracking-wide">soon</span>}
                      </span>
                    ) : link.internal ? (
                      <Link to={link.to}
                        className="font-sans font-light text-paper/50 text-sm hover:text-paper/80 transition-colors duration-150">
                        {link.label}
                      </Link>
                    ) : (
                      <button
                        onClick={() => link.sectionId && document.getElementById(link.sectionId)?.scrollIntoView({ behavior: 'smooth' })}
                        className="font-sans font-light text-paper/50 text-sm hover:text-paper/80 transition-colors duration-150"
                      >
                        {link.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="font-mono text-paper/30 text-[11px] tracking-wide">
            © {currentYear} FinInteract. All rights reserved.
          </p>
          {!company && (
            <Link to="/" className="font-mono text-paper/30 text-[11px] tracking-wide hover:text-paper/50 transition-colors duration-150">
              fininteract.com
            </Link>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
